import {
  AlertCircle,
  ChevronDown,
  Download,
  FileSpreadsheet,
  Upload,
  UploadIcon,
} from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Dropzone, DropzoneContent } from '@/components/ui/file-dropzone';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useCreateCampaign } from '@/hooks/useCampaigns';
import { useSchema } from '@/hooks/useSchema';
import { useStore } from '@/store';
import {
  generateDefaultCampaignName,
  MAX_CAMPAIGN_ROWS,
  processExcelFile,
  type ValidationResult,
} from '@/utils/campaign';
import {
  EMPTY_VALUE_MARKER,
  flattenSchemaFields,
  hasRepeatedFields,
  MULTI_VALUE_DELIMITER,
  withReferenceIdColumn,
} from '@/utils/campaignFields';
import {
  downloadWorkflowSample,
  type SampleFieldDefinition,
  type SampleFileFormat,
} from '@/utils/excel';

interface BulkCreateCasesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function BulkCreateCasesModal({ open, onOpenChange, onSuccess }: BulkCreateCasesModalProps) {
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [campaignName, setCampaignName] = React.useState('');
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [isUploading, setIsUploading] = React.useState(false);
  const [validationResult, setValidationResult] = React.useState<ValidationResult | null>(null);
  const [parsedData, setParsedData] = React.useState<any[]>([]);
  const [apiError, setApiError] = React.useState<string | null>(null);

  const { getSelectedWorkflow, getSelectedOrganization, getSelectedProgram } = useStore();
  const selectedWorkflow = getSelectedWorkflow();

  const titleRef = React.useRef<HTMLHeadingElement>(null);
  const NAME_SIZE_STEPS = ['text-lg', 'text-base', 'text-sm', 'text-xs'] as const;
  const [nameSizeIdx, setNameSizeIdx] = React.useState(0);

  React.useLayoutEffect(() => {
    setNameSizeIdx(0);
  }, [selectedWorkflow?.name, open]);

  React.useLayoutEffect(() => {
    const t = titleRef.current;
    if (!t) return;
    if (t.scrollWidth > t.clientWidth + 1 && nameSizeIdx < NAME_SIZE_STEPS.length - 1) {
      setNameSizeIdx(idx => idx + 1);
    }
  });
  const createCampaign = useCreateCampaign();

  // Fetch schema from schema service
  const { inputGroups } = useSchema(selectedWorkflow?.id);

  // Get organization and program using the getter methods
  const selectedOrg = getSelectedOrganization();
  const selectedProgram = getSelectedProgram();

  // Generate default campaign name when modal opens
  React.useEffect(() => {
    if (open) {
      const defaultName = generateDefaultCampaignName(selectedOrg?.name, selectedProgram?.name);
      setCampaignName(defaultName);
      setApiError(null); // Clear any previous API errors
    }
  }, [open, selectedOrg?.name, selectedProgram?.name]);

  // Get workflow field definitions from UI schema (with id, placeholder, options).
  // Nested array/object fields contribute their leaves under a dotted id
  // (`cpt_codes.code`) so the template matches what the server parses.
  const workflowFields = React.useMemo((): SampleFieldDefinition[] => {
    if (inputGroups.length === 0) return [];

    return inputGroups.flatMap(group => flattenSchemaFields(group.fields));
  }, [inputGroups]);

  const showsRepeatedHint = React.useMemo(
    () => hasRepeatedFields(workflowFields),
    [workflowFields],
  );

  // Derive expected headers for file validation
  const expectedHeaders = React.useMemo(() => workflowFields.map(f => f.id), [workflowFields]);

  const handleFileSelect = async (files: File[]) => {
    if (files.length === 0) return;

    const file = files[0];
    setSelectedFile(file);
    setValidationResult(null);
    setApiError(null); // Clear any previous API errors
    setParsedData([]);

    // Process the file immediately after selection
    setIsProcessing(true);
    try {
      const result = await processExcelFile(file, expectedHeaders);
      setValidationResult(result);
      if (result.data) {
        setParsedData(result.data);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileRemove = () => {
    setSelectedFile(null);
    setValidationResult(null);
    setParsedData([]);
  };

  const downloadSample = async (format: SampleFileFormat) => {
    const orgName = selectedOrg?.name || 'org';
    const programName = selectedProgram?.name || 'program';
    const sampleFields = withReferenceIdColumn(workflowFields, selectedWorkflow?.workflow_type);
    await downloadWorkflowSample(sampleFields, format, orgName, programName);
  };

  const handleUpload = async () => {
    if (!selectedFile || !validationResult?.isValid || !parsedData.length) {
      return;
    }

    if (!selectedWorkflow) {
      toast.error('Please select a workflow first');
      return;
    }

    if (!campaignName.trim()) {
      toast.error('Please enter a campaign name');
      return;
    }

    setIsUploading(true);
    setApiError(null); // Clear any previous errors before starting

    try {
      // Create the campaign with the file - pass the File object directly
      await createCampaign.mutateAsync({
        name: campaignName,
        workflow_id: selectedWorkflow.id!,
        file_data: selectedFile, // Pass the File object directly
      });

      toast.success(`Campaign created successfully`);

      if (onSuccess) {
        onSuccess();
      }

      // Close modal on success
      handleClose();
    } catch (error: any) {
      console.error('Error creating campaign:', error);

      // Extract error message from the response
      let errorMessage = 'Failed to create campaign.';

      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      setApiError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setSelectedFile(null);
      // Campaign name will be regenerated when modal opens again
      setCampaignName('');
      setValidationResult(null);
      setParsedData([]);
      setApiError(null); // Clear API errors when closing
      onOpenChange(false);
    }
  };

  const canUpload =
    selectedFile && validationResult?.isValid && campaignName.trim() && !isUploading;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-10">
        <DialogHeader>
          <div className="flex items-center justify-between gap-2">
            <DialogTitle
              ref={titleRef}
              className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden"
            >
              <FileSpreadsheet className="h-5 w-5 shrink-0" />
              <span className="shrink-0 whitespace-nowrap">Create campaign</span>
              {selectedWorkflow?.name && (
                <span className={`whitespace-nowrap ${NAME_SIZE_STEPS[nameSizeIdx]}`}>
                  <span className="text-muted-foreground font-normal">— </span>
                  {selectedWorkflow.name}
                </span>
              )}
            </DialogTitle>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={expectedHeaders.length === 0}
                  className="flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download Sample
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => downloadSample('excel')}
                  className="flex items-center gap-2"
                >
                  Excel (.xlsx)
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => downloadSample('csv')}
                  className="flex items-center gap-2"
                >
                  CSV (.csv)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <DialogDescription>
            Upload an Excel or CSV file with multiple case inputs to create your campaign.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4  flex-1">
          {/* Campaign Name Input */}
          <div className="space-y-2">
            <Label htmlFor="campaign-name">Campaign Name</Label>
            <Input
              id="campaign-name"
              placeholder="Enter campaign name"
              value={campaignName}
              onChange={e => setCampaignName(e.target.value)}
              disabled={isUploading}
            />
          </div>

          {/* File Upload Dropzone */}
          <div className="space-y-2">
            <Dropzone
              onDrop={handleFileSelect}
              accept={{
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                'application/vnd.ms-excel': ['.xls'],
                'text/csv': ['.csv'],
              }}
              maxFiles={1}
              disabled={isProcessing || isUploading}
              src={selectedFile ? [selectedFile] : undefined}
            >
              {selectedFile ? (
                <DropzoneContent />
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <div className="flex size-8 items-center justify-center rounded-md bg-muted text-muted-foreground">
                    <UploadIcon size={16} />
                  </div>
                  <p className="my-2 w-full truncate text-wrap font-medium text-sm text-center">
                    Upload a file
                  </p>
                  <p className="w-full truncate text-wrap text-muted-foreground text-xs text-center">
                    Drag and drop or click to upload
                  </p>
                </div>
              )}
            </Dropzone>
            {selectedFile && !isUploading && (
              <Button variant="ghost" size="sm" onClick={handleFileRemove} className="w-full">
                Remove file
              </Button>
            )}
          </div>

          {/* Expected Headers Info */}
          {expectedHeaders.length > 0 && !selectedFile && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-2">
                  {showsRepeatedHint && (
                    <p className="text-muted-foreground">
                      Columns like{' '}
                      <code className="px-1 py-0.5 bg-muted rounded text-xs">cpt_codes.code</code>{' '}
                      can hold several values separated by{' '}
                      <code className="px-1 py-0.5 bg-muted rounded text-xs">
                        {MULTI_VALUE_DELIMITER}
                      </code>
                      . Every column of the same group must list the same number of values, in the
                      same order — use{' '}
                      <code className="px-1 py-0.5 bg-muted rounded text-xs">
                        {EMPTY_VALUE_MARKER}
                      </code>{' '}
                      where a value is missing (
                      <code className="px-1 py-0.5 bg-muted rounded text-xs">{`96413${MULTI_VALUE_DELIMITER}${EMPTY_VALUE_MARKER}${MULTI_VALUE_DELIMITER}96417`}</code>
                      ), and{' '}
                      <code className="px-1 py-0.5 bg-muted rounded text-xs">{`\\${MULTI_VALUE_DELIMITER}`}</code>{' '}
                      for a literal separator.
                    </p>
                  )}
                  <p className="text-muted-foreground">Up to {MAX_CAMPAIGN_ROWS} rows per file.</p>
                  <p className="font-medium">Suggested columns:</p>
                  <ScrollArea className="h-24">
                    <div className="flex flex-wrap gap-2">
                      {expectedHeaders.map(header => (
                        <code key={header} className="px-2 py-1 bg-muted rounded text-xs">
                          {header}
                        </code>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Validation Results */}
          {validationResult && (
            <div className="space-y-2">
              {validationResult.errors.length > 0 && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <AlertDescription>
                    <div className="space-y-1">
                      {validationResult.errors.map((error, index) => (
                        <p key={index}>{error}</p>
                      ))}
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {validationResult.warnings.length > 0 && (
                <Alert className="flex items-start gap-2 p-3">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <AlertDescription className="flex-1">
                    <div className="space-y-1">
                      {validationResult.warnings.map((warning, index) => (
                        <p key={index}>{warning}</p>
                      ))}
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* API Error Alert */}
          {apiError && !isUploading && (
            <Alert variant="destructive" className="flex items-start gap-2 p-3">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <AlertDescription className="flex-1">{apiError}</AlertDescription>
            </Alert>
          )}

          {/* Upload Progress */}
          {isUploading && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Creating campaign...</p>
              <Progress value={100} className="animate-pulse" />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isUploading}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={!canUpload} className="gap-2">
            {isUploading ? (
              <>Creating Campaign...</>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                Create Campaign
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
